import {
  IonAvatar,
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import { checkmarkCircle, share, timerOutline } from 'ionicons/icons';
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../utils/api";

// Import the custom components
import { t } from "i18next";
import { GolfAvatar } from "../ui/GolfAvatar";
import Scorecard from './Scorecard';
import NeoCarousel from "../ui/neoCarousel";
import ComingSoonStats from "./ComingSoonStats";
import { FairwayAccuracy } from "./FairwayAccuracy";
import ScoreByPar from "./ScoreByPar";
import ScoreDistribution from "./ScoreDistribution";

// Interface definitions for player data
interface HoleScore {
  hole: number;
  par: number;
  score: number;
  putts?: number | null;
  fairwayHit?: 'left' | 'center' | 'right' | null;
  greenInRegulation?: boolean | null;
}

export interface PlayerStats {
  eagles: number;
  birdies: number;
  pars: number;
  bogeys: number;
  doubleBogeys: number;
  totalPutts: number;
  holesWithPuttsData: number;
  fairwaysHitCenter: number;
  fairwayOpportunities: number;
  greensInRegulationCount: number;
  girOpportunities: number;
}

export interface PlayerResult {
  id: string;
  userId?: string;
  name: string;
  avatar: string;
  totalScore: number;
  netScore?: number;
  toPar?: number;
  scores: HoleScore[];
  stats: PlayerStats;
  status?: string; // playing, invited, cancelled, finished
}

const GolfResultScreen: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();

  // State variables
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [gameData, setGameData] = useState<any>(null);
  const [players, setPlayers] = useState<PlayerResult[]>([]);

  // Fetch game data when component mounts
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        // Fetch game data from the API
        const response = await api.get(`/game/${gameId}`);
        const data = response.data;

        // Set game data
        setGameData(data);
        if (data.players && Array.isArray(data.players)) {
          const processedPlayers = data.players.map((player: any) => {
            // Process player scores
            const playerScores: HoleScore[] = [];
            let totalScore = 0;
            let eagles = 0;
            let birdies = 0;
            let pars = 0;
            let bogeys = 0;
            let doubleBogeys = 0;

            let statTotalPutts = 0;
            let statHolesWithPuttsData = 0;
            let statFairwayLeft = 0;
            let statFairwayMiddle = 0;
            let statFairwayRight = 0;
            let statFairwayOpportunities = 0;
            let statGreensInRegulationCount = 0;
            let statGirOpportunities = 0;

            // Get holes data from the first tee (assuming all players use same tee structure)
            const holesByTee = data.holesByTee || {};
            const holes = Object.values(holesByTee)[0] || [];
            const holesCount = Array.isArray(holes) ? holes.length : 0;

            // Iterate through holes to get player scores
            const scores = data.scores || {};

            for (let holeNumber = 1; holeNumber <= holesCount; holeNumber++) {
              const holeScores = scores[holeNumber] || {};
              const playerScore = player.userId ? holeScores[player.userId] || {} : {};
              // Safely access the hole information with proper type checking
              const hole = Array.isArray(holes) && holes.length >= holeNumber ? holes[holeNumber - 1] || {} : {};

              if (playerScore.score) {
                totalScore += playerScore.score;

                // Calculate score relative to par
                const scoreDiff = playerScore.score - hole.par;
                if (scoreDiff <= -2) eagles++;
                else if (scoreDiff === -1) birdies++;
                else if (scoreDiff === 0) pars++;
                else if (scoreDiff === 1) bogeys++;
                else doubleBogeys++;
              }

              // Calculate stats for current hole
              if (playerScore.putts !== null && playerScore.putts !== undefined) {
                statTotalPutts += playerScore.putts;
                statHolesWithPuttsData++;
              }

              if (hole.par === 4 || hole.par === 5) {
                if (playerScore.fairwayHit !== null && playerScore.fairwayHit !== undefined) {
                  statFairwayOpportunities++;
                  if (playerScore.fairwayHit === 'center') {
                    statFairwayMiddle++;
                  } else if (playerScore.fairwayHit === 'left') {
                    statFairwayLeft++;
                  } else if (playerScore.fairwayHit === 'right') {
                    statFairwayRight++;
                  }
                }
              }

              if (playerScore.score) { // If score exists for the hole
                statGirOpportunities++;
                if (playerScore.greenInRegulation === true) {
                  statGreensInRegulationCount++;
                }
              }

              playerScores.push({
                hole: holeNumber,
                par: hole.par || 0,
                score: playerScore.score || 0,
                putts: playerScore.putts,
                fairwayHit: playerScore.fairwayHit,
                greenInRegulation: playerScore.greenInRegulation
              });
            }

            return {
              id: player.id,
              userId: player.userId,
              name: player.name || 'Player',
              avatar: player.avatar || '',
              totalScore,
              scores: playerScores,
              stats: {
                eagles,
                birdies,
                pars,
                bogeys,
                doubleBogeys,
                totalPutts: statTotalPutts,
                holesWithPuttsData: statHolesWithPuttsData,
                fairwayAccuracy: {
                  left: statFairwayLeft,
                  middle: statFairwayMiddle,
                  right: statFairwayRight,
                  total: statFairwayOpportunities
                },
                greensInRegulationCount: statGreensInRegulationCount,
                girOpportunities: statGirOpportunities
              },
              status: player.status || 'playing'
            };
          });

          setPlayers(processedPlayers);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching game data:', error);
        setError('Failed to load game data. Please try again.');
        setLoading(false);
      }
    };

    if (gameId) {
      fetchGameData();
    }
  }, [gameId]);

  // Sort players by score for the leaderboard
  const sortedPlayers = [...players].sort((a, b) => a.totalScore - b.totalScore);

  return (
    <IonPage>
      <IonHeader translucent={true} className="ion-no-border">
        <IonToolbar color="dark">
          <IonButtons slot="start">
            <IonBackButton text="" defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle className="text-lg font-semibold">{t('game_result')}</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear">
              <IonIcon icon={share} slot="start" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen={true} color="dark">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">{error}</div>
        ) : (
          <div className="p-2 max-w-3xl mx-auto mb-safe">
            {/* Game Info Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">{gameData?.game.clubName}</h2>
                <h2 className="text-xl font-bold">{gameData?.game.courseName}</h2>
                <div className="text-xs text-gray-500">{new Date(gameData?.game.date).toLocaleDateString()}</div>
              </div>
              <IonChip className="hidden" color="success">
                <IonIcon icon={gameData?.game.status === 'finished' ? checkmarkCircle : timerOutline} />
                {gameData?.game.status === 'finished' ? (
                  <IonLabel className="capitalize">{t(gameData?.game.status?.toLowerCase())}</IonLabel>
                ) : (
                  <IonLabel className="capitalize">{t(gameData?.game.status?.toLowerCase())}</IonLabel>
                )}
              </IonChip>
            </div>

            {/* SECTION 1: Scorecard (always shown for first player) */}
            {sortedPlayers.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <GolfAvatar
                    name={sortedPlayers[0].name}
                    imageUrl={sortedPlayers[0].avatar}
                    size={48}
                  />
                  {sortedPlayers[0].name}
                </h3>
                <Scorecard player={sortedPlayers[0]} />
              </div>
            )}

            {/* SECTION 2: Leaderboard (only if more than 1 player) */}
            {sortedPlayers.length > 1 && (
              <div className="mb-6 mt-6">
                <h3 className="text-lg font-semibold mb-3">{t('leaderboard')}</h3>
                <div className="bg-card rounded-2xl overflow-hidden">
                  {sortedPlayers.map((player, index) => (
                    <div key={player.id} className="flex items-center p-3 border-b last:border-b-0">
                      <div className="w-8 text-center font-semibold">{index + 1}</div>
                      <IonAvatar className="w-8 h-8 mx-2">
                        <img src={player.avatar || 'https://ionicframework.com/docs/img/demos/avatar.svg'} alt="" className="w-full h-full object-cover" />
                      </IonAvatar>
                      <div className="flex-grow">
                        <div className="font-medium">{player.name}</div>
                        <div className="text-xs text-gray-500">
                          {player.status === 'finished' ? 'Finished' : 'Still playing'}
                        </div>
                      </div>
                      <div className="text-xl font-bold">{player.totalScore}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <h3 className="text-lg font-semibold mb-3">{t('stats')}</h3>

            {/* SECTION 3: Stats for first player */}
            {sortedPlayers.length > 0 && (
              <>
                {/* <div className="mb-6 mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2"> */}

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 my-4">
                  <div className="bg-card rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium mb-2">{t('fairways_hit')}</div>
                    <div className="text-2xl font-bold">
                      {sortedPlayers[0].stats.fairwayOpportunities > 0
                        ? ((sortedPlayers[0].stats.fairwaysHitCenter / sortedPlayers[0].stats.fairwayOpportunities) * 100).toFixed(0)
                        : '0'}
                      <span className="text-sm font-normal">%</span>
                    </div>
                  </div>
                  <div className="bg-card rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium mb-2">{t('green_in_regulation')}</div>
                    <div className="text-2xl font-bold">
                      {sortedPlayers[0].stats.girOpportunities > 0
                        ? ((sortedPlayers[0].stats.greensInRegulationCount / sortedPlayers[0].stats.girOpportunities) * 100).toFixed(0)
                        : '0'}
                      <span className="text-sm font-normal">%</span>
                    </div>
                  </div>
                  <div className="bg-card rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium mb-2">Putts</div>
                    <div className="text-2xl font-bold">
                      {sortedPlayers[0].stats.totalPutts}
                    </div>
                  </div>
                  <div className="bg-card rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium mb-2">{t('average_putts')}</div>
                    <div className="text-2xl font-bold">
                      {sortedPlayers[0].stats.holesWithPuttsData > 0
                        ? (sortedPlayers[0].stats.totalPutts / sortedPlayers[0].stats.holesWithPuttsData).toFixed(1)
                        : '0.0'}
                    </div>
                  </div>
                </div>
                <NeoCarousel enableArrows={false} images={[]} className="w-full" indicatorPosition="below">
                  <ScoreDistribution className="w-full" stats={sortedPlayers[0].stats} />

                  <FairwayAccuracy className="w-full" stats={sortedPlayers[0].stats} />
                  <ScoreByPar className="w-full shadow-sm" player={sortedPlayers[0]} />
                  <div className="w-full h-[288px] justify-center items-center bg-card rounded-2xl">
                    <ComingSoonStats className="size-full" />
                  </div>
                </NeoCarousel>
              </>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default GolfResultScreen;
